/**
 * TCGdex backend client — hits api.tcgdex.net directly.
 *
 * Used for foreign-language card requests (ja, fr, de, it, es, pt) and as a
 * fallback when digitalcards misses. Kept small and stateless.
 */
const BASE = 'https://api.tcgdex.net/v2';
const ASSETS = 'https://assets.tcgdex.net';

const endpoint = (language) => `${BASE}/${language}`;

/**
 * Build the canonical image URL for a card. Handles the TCGdex CDN pattern.
 * @returns {string}
 */
function buildImageUrl(setId, localId, language, resolution = 'high') {
  return `${ASSETS}/${language}/${setId}/${localId}/${resolution}.webp`;
}

/**
 * Normalize a TCGdex API card record into the shared Card shape.
 * @returns {import('./types').Card}
 */
function normalizeCard(raw, language) {
  const setId = raw.set?.id || raw.set_id || null;
  const setName = raw.set?.name || raw.set_name || null;
  const localId = raw.localId || raw.local_id || null;

  return {
    id: raw.id,
    set_id: setId,
    set_name: setName,
    local_id: localId,
    name: raw.name,
    category: raw.category,
    rarity: raw.rarity,
    hp: raw.hp ?? null,
    types: raw.types,
    stage: raw.stage,
    illustrator: raw.illustrator,
    national_pokedex: raw.dexId,
    regulation_mark: raw.regulationMark,
    attacks: raw.attacks,
    weaknesses: raw.weaknesses,
    resistances: raw.resistances,
    retreat_cost: raw.retreat,
    abilities: raw.abilities,
    description: raw.description,
    legal_standard: raw.legal?.standard,
    legal_expanded: raw.legal?.expanded,
    image_url: setId && localId ? buildImageUrl(setId, localId, language) : (raw.image ? `${raw.image}/high.webp` : null),
    _source: 'tcgdex',
  };
}

/** @returns {Promise<import('./types').Card | null>} */
export async function getCard(cardId, { language = 'en' } = {}) {
  const url = `${endpoint(language)}/cards/${cardId}`;
  const res = await fetch(url);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`TCGdex getCard ${cardId} → HTTP ${res.status}`);
  const raw = await res.json();
  return normalizeCard(raw, language);
}

/** @returns {Promise<import('./types').Card[]>} */
export async function searchCards(query, { language = 'en', limit = 50 } = {}) {
  const q = encodeURIComponent(query.trim());
  const url = `${endpoint(language)}/cards?name=${q}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`TCGdex searchCards "${query}" → HTTP ${res.status}`);
  const raw = await res.json();
  const slice = Array.isArray(raw) ? raw.slice(0, limit) : [];
  return slice.map((c) => normalizeCard(c, language));
}

/** @returns {Promise<import('./types').Set | null>} */
export async function getSet(setId, { language = 'en', includeCards = false } = {}) {
  const url = `${endpoint(language)}/sets/${setId}`;
  const res = await fetch(url);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`TCGdex getSet ${setId} → HTTP ${res.status}`);
  const raw = await res.json();

  const set = {
    id: raw.id,
    name: raw.name,
    era_id: raw.serie?.id,
    abbreviation: raw.abbreviation?.official || raw.abbreviation?.short,
    serie_id: raw.serie?.id,
    release_date: raw.releaseDate,
    card_count_total: raw.cardCount?.total,
    card_count_official: raw.cardCount?.official,
    logo_url: raw.logo,
    _source: 'tcgdex',
  };
  if (includeCards && Array.isArray(raw.cards)) {
    set.cards = raw.cards.map((c) => normalizeCard({ ...c, set: { id: raw.id } }, language));
  }
  return set;
}

/** @returns {Promise<import('./types').Set[]>} */
export async function getSets({ language = 'en' } = {}) {
  const url = `${endpoint(language)}/sets?sort:field=releaseDate&sort:order=DESC`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`TCGdex getSets → HTTP ${res.status}`);
  const raw = await res.json();
  const list = Array.isArray(raw) ? raw : [];
  return list.map((s) => ({
    id: s.id,
    name: s.name,
    era_id: s.serie?.id,
    release_date: s.releaseDate,
    card_count_total: s.cardCount?.total,
    card_count_official: s.cardCount?.official,
    logo_url: s.logo,
    _source: 'tcgdex',
  }));
}

/**
 * TCGdex does not expose per-variant image URLs — it serves one image per card.
 * We build the canonical CDN URL from setId + localId.
 * @returns {Promise<string | null>}
 */
export async function getCardImage(cardId, _variantId = null, { language = 'en', resolution = 'high' } = {}) {
  // Card IDs are formatted `{setId}-{localId}` — split for URL construction.
  const idx = cardId.lastIndexOf('-');
  if (idx < 0) return null;
  const setId = cardId.slice(0, idx);
  const localId = cardId.slice(idx + 1);
  return buildImageUrl(setId, localId, language, resolution);
}

/**
 * TCGdex does not model Brent-style variant structure.
 * Returns an empty array — consumers should handle "no variants" gracefully.
 */
export async function getCardVariants(_cardId) {
  return [];
}

/**
 * TCGdex has no eras concept — it has `series` at the endpoint level, but the
 * curated 13-era structure lives only in digitalcards. Returns empty.
 */
export async function getEras() {
  return [];
}
